import { z } from 'zod';
import { DEFAULT_COMPANY_LOCALE, isValidPostcode, type CompanyLocale } from '@/utils/locale';

/**
 * Customer form schema for the tenant's country: the postal-code rule follows
 * the locale (4 digits in AU/NZ, ZIP or ZIP+4 in the US, alphanumeric in the
 * UK and Canada). Build it with the locale from `useCompanyLocale()`.
 */
export function createCustomerSchema(locale: CompanyLocale = DEFAULT_COMPANY_LOCALE) {
  return z.object({
    customer_type: z.enum(['individual', 'company']),
    customer_name: z.string().min(1, 'Customer name is required'),
    company_name: z.string().optional(),
    primary_phone: z.string().min(1, 'Primary phone is required'),
    secondary_phone: z.string().optional(),
    primary_email: z.string().email('Invalid email').optional().or(z.literal('')),
    secondary_email: z.string().email('Invalid email').optional().or(z.literal('')),
    primary_address: z.string().min(1, 'Street address is required'),
    suburb: z.string().optional(),
    state: z.string().optional(),
    postcode: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine((v) => isValidPostcode(v, locale), locale.postcode.message),
    secondary_address: z.string().optional(),
    tags: z.array(z.string()).optional(),
    notes: z.string().optional(),
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
  });
}

/** Australian-default schema, kept for callers that have no tenant context. */
export const customerSchema = createCustomerSchema();

export type CustomerFormData = z.infer<typeof customerSchema>;
